// Produkt an Kunden verkaufen (08/2026) — sofort ODER auf nächste Rechnung
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const SellProductDialog = ({ open, onOpenChange, customer, onSold }) => {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [billingMode, setBillingMode] = useState('naechste_rechnung');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/products`, { headers: authHeaders() })
      .then((res) => {
        setProducts(res.data || []);
        if (res.data?.length && !productId) setProductId(res.data[0].id);
      })
      .catch(() => toast.error('Produkte konnten nicht geladen werden'));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = products.find((p) => p.id === productId);
  const total = selected ? (selected.price * quantity).toFixed(2) : '0.00';

  const sell = async () => {
    if (!productId || !customer?.id) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/products/sell`, null, {
        headers: authHeaders(),
        params: {
          product_id: productId,
          customer_id: customer.id,
          quantity,
          billing_mode: billingMode,
        },
      });
      toast.success(res.data.message);
      onOpenChange(false);
      setQuantity(1);
      onSold && onSold();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verkauf fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sell-product-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-indigo-600" />
            Produkt verkaufen — {customer?.first_name} {customer?.last_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Produkt</label>
            <select
              data-testid="sell-product-select"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name} — CHF {Number(p.price).toFixed(2)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Menge</label>
            <input
              data-testid="sell-product-quantity"
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              className="mt-1 w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Verrechnung</label>
            <label className="flex items-start gap-2 p-2.5 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                data-testid="sell-billing-next-invoice"
                type="radio"
                checked={billingMode === 'naechste_rechnung'}
                onChange={() => setBillingMode('naechste_rechnung')}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium">Auf nächste Rechnung</span><br />
                <span className="text-gray-500 text-xs">Wird automatisch mit der Tagesrechnung (Check-in) oder Monatsrechnung verrechnet</span>
              </span>
            </label>
            <label className="flex items-start gap-2 p-2.5 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                data-testid="sell-billing-immediate"
                type="radio"
                checked={billingMode === 'sofort'}
                onChange={() => setBillingMode('sofort')}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium">Sofort eigene Rechnung</span><br />
                <span className="text-gray-500 text-xs">Erstellt direkt eine separate Rechnung</span>
              </span>
            </label>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold" data-testid="sell-product-total">Total: CHF {total}</span>
            <Button data-testid="sell-product-confirm-btn" onClick={sell} disabled={busy || !productId}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Verkaufen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SellProductDialog;
